/**
 * Circuit Breaker Pattern Implementation
 *
 * Prevents cascading failures by stopping requests to failing services.
 * When a service fails repeatedly, the circuit "opens" and blocks requests
 * for a timeout period, then allows a single test request through.
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Service failing, requests blocked
 * - HALF_OPEN: Testing with single request after timeout
 *
 * LIMITATIONS:
 * - This implementation is designed for browser environments with low concurrency.
 * - In the HALF_OPEN state, multiple synchronous calls to check() could theoretically
 *   both pass before either sets halfOpenRequestInFlight=true (race condition).
 * - This is acceptable for browser-based circuit breakers where true simultaneous
 *   calls are rare, but would need locking/atomics for high-concurrency environments.
 */

import errorTracker from "./errorTracker";

// Circuit breaker states
export const CircuitState = {
  CLOSED: "closed",
  OPEN: "open",
  HALF_OPEN: "half_open",
};

// Default configuration - can be overridden per instance
const DEFAULT_CONFIG = {
  failureThreshold: 5, // Open circuit after this many failures
  resetTimeoutMs: 30000, // Try again after 30 seconds
};

// Standard user-facing error messages for circuit breaker errors
export const CIRCUIT_BREAKER_MESSAGES = {
  SERVICE_UNAVAILABLE:
    "Service temporarily unavailable. Please try again later.",
  AUTH_UNAVAILABLE:
    "Authentication service temporarily unavailable. Please try again later.",
  NETWORK_ERROR: "Network error. Please check your internet connection.",
  OFFLINE: "You appear to be offline. Please check your connection.",
};

/**
 * Check if an error is a circuit breaker error.
 * Use this instead of duplicating the check pattern across components.
 * @param {Error|null} err - The error to check
 * @returns {boolean} True if this is a circuit breaker error
 */
export function isCircuitBreakerError(err) {
  if (!err) return false;
  return err.isCircuitBreakerError === true || err.code === "CIRCUIT_OPEN";
}

/**
 * Create a circuit breaker instance for a specific service.
 *
 * @param {string} name - Name of the service (for logging)
 * @param {Object} config - Configuration overrides
 * @param {number} config.failureThreshold - Failures before opening circuit
 * @param {number} config.resetTimeoutMs - Time before testing again
 * @returns {Object} Circuit breaker instance with check, recordSuccess, recordFailure methods
 */
export function createCircuitBreaker(name, config = {}) {
  const settings = { ...DEFAULT_CONFIG, ...config };

  // Circuit state (internal)
  const state = {
    failures: 0,
    lastFailureTime: 0,
    circuitState: CircuitState.CLOSED,
    halfOpenRequestInFlight: false,
  };

  /**
   * Check if a request should be allowed through.
   * @returns {boolean} true if request is allowed, false if blocked
   */
  function check() {
    if (state.circuitState === CircuitState.CLOSED) {
      return true;
    }

    if (state.circuitState === CircuitState.OPEN) {
      const now = Date.now();
      if (now - state.lastFailureTime > settings.resetTimeoutMs) {
        // Transition to half-open - allow ONE test request through
        state.circuitState = CircuitState.HALF_OPEN;
        state.halfOpenRequestInFlight = true;
        return true;
      }
      return false;
    }

    if (state.circuitState === CircuitState.HALF_OPEN) {
      // Only allow one request through in half-open state
      // If one is already in flight, block additional requests
      if (state.halfOpenRequestInFlight) {
        return false;
      }
      state.halfOpenRequestInFlight = true;
      return true;
    }

    return false;
  }

  /**
   * Record a failed request. May open the circuit if threshold is reached.
   */
  function recordFailure() {
    state.failures++;
    state.lastFailureTime = Date.now();
    state.halfOpenRequestInFlight = false;

    if (state.circuitState === CircuitState.HALF_OPEN) {
      // Test request failed - go back to open
      state.circuitState = CircuitState.OPEN;
      errorTracker.captureMessage(
        `[${name}] Circuit breaker re-opened after half-open test failed`,
        { level: "warning" },
      );
    } else if (state.failures >= settings.failureThreshold) {
      state.circuitState = CircuitState.OPEN;
      errorTracker.captureMessage(
        `[${name}] Circuit breaker opened - too many failures`,
        {
          level: "warning",
          extra: { failures: state.failures },
        },
      );
    }
  }

  /**
   * Record a successful request. Resets failure count and closes circuit.
   */
  function recordSuccess() {
    const wasHalfOpen = state.circuitState === CircuitState.HALF_OPEN;
    state.failures = 0;
    state.circuitState = CircuitState.CLOSED;
    state.halfOpenRequestInFlight = false;

    if (wasHalfOpen) {
      errorTracker.captureMessage(
        `[${name}] Circuit breaker closed - service recovered`,
        { level: "info" },
      );
    }
  }

  /**
   * Get current circuit state (for debugging/monitoring).
   * @returns {Object} Current state snapshot
   */
  function getState() {
    return {
      name,
      circuitState: state.circuitState,
      failures: state.failures,
      lastFailureTime: state.lastFailureTime,
      config: settings,
    };
  }

  /**
   * Reset the circuit breaker to initial state.
   * Useful for testing or manual recovery.
   */
  function reset() {
    state.failures = 0;
    state.lastFailureTime = 0;
    state.circuitState = CircuitState.CLOSED;
    state.halfOpenRequestInFlight = false;
  }

  return {
    check,
    recordSuccess,
    recordFailure,
    getState,
    reset,
  };
}

/**
 * Create an error object for circuit breaker rejections.
 * @param {string} message - User-friendly error message
 * @returns {Error} Error with circuit breaker properties
 */
export function createCircuitBreakerError(message) {
  const error = new Error(message);
  error.code = "CIRCUIT_OPEN";
  error.isCircuitBreakerError = true;
  return error;
}
